import React, { useEffect, useState } from 'react';
import { getStrategyReport, StrategyReport } from '../api/strategy';
import {
  MetricTiles, MetricsRow, HonestyNote, fmtSignedPct, signClass,
} from './StrategyReportShared';
import './StrategyReports.css';

// Cumulative live/paper P&L as a tiny inline sparkline (no chart lib).
const EquitySpark: React.FC<{ curve: StrategyReport['equity_curve'] }> = ({ curve }) => {
  if (!curve.length) return <p className="sr-empty">No settled days yet.</p>;
  const W = 600, H = 110, pad = 6;
  const xs = curve.map((_, i) => i);
  const vals = curve.flatMap((d) => [d.live, d.paper]);
  const min = Math.min(0, ...vals), max = Math.max(0, ...vals);
  const span = max - min || 1;
  const x = (i: number) => pad + (xs.length === 1 ? 0 : (i / (xs.length - 1)) * (W - 2 * pad));
  const y = (v: number) => H - pad - ((v - min) / span) * (H - 2 * pad);
  const path = (key: 'live' | 'paper') =>
    curve.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d[key]).toFixed(1)}`).join(' ');
  const zeroY = y(0);
  return (
    <>
      <svg className="sr-equity" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <line x1={pad} y1={zeroY} x2={W - pad} y2={zeroY} stroke="#e2e8f0" strokeWidth={1} />
        <path d={path('paper')} fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="4 3" />
        <path d={path('live')} fill="none" stroke="#2563eb" strokeWidth={2} />
      </svg>
      <div className="sr-equity-legend">
        <span><i style={{ background: '#2563eb' }} />Live (banked)</span>
        <span><i style={{ background: '#cbd5e1' }} />Paper (hypothetical)</span>
      </div>
    </>
  );
};

const StrategyResults: React.FC = () => {
  const [report, setReport] = useState<StrategyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [aggMode, setAggMode] = useState<'both' | 'live' | 'paper'>('both');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setReport(await getStrategyReport({ from: from || undefined, to: to || undefined }));
    } catch (e: any) {
      setError(e?.message || 'Could not load the report');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const agg = report?.aggregate[aggMode];
  const sim = report?.staking_sim;
  const simRow = (label: string, book?: StrategyReport['staking_sim']['all']) =>
    book ? (
      <tr>
        <td className="sr-row-label">{label}</td>
        <td className="sr-num">{book.n}</td>
        <td className={`sr-num ${signClass(book.flat.roi)}`}>{fmtSignedPct(book.flat.roi)}</td>
        <td className={`sr-num ${signClass(book.quarter_kelly.roi)}`}>{fmtSignedPct(book.quarter_kelly.roi)}</td>
        <td className={`sr-num ${signClass(book.dutch.roi)}`}>{fmtSignedPct(book.dutch.roi)}</td>
      </tr>
    ) : null;

  const corr = report?.engine_correlation;

  return (
    <div className="sr-page">
      <div className="sr-head">
        <h1>Strategy Results</h1>
        <p className="sr-sub">
          Deterministic report across all your strategy runs — engine breakdowns, equity curve,
          staking comparison and closing-line value.
        </p>
      </div>

      <div className="sr-controls">
        <label>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button onClick={load}>Apply</button>
        {(from || to) && (
          <button className="ghost" onClick={() => { setFrom(''); setTo(''); setTimeout(load, 0); }}>Clear</button>
        )}
      </div>

      {loading && <div className="sr-loading">Loading…</div>}
      {error && <div className="sr-error">{error}</div>}

      {report && !loading && (
        report.total_records === 0 ? (
          <div className="sr-empty">
            No settled bets in this range yet. The report fills in as runs are placed (live) or scored
            on review (paper).
          </div>
        ) : (
          <>
            <section className="sr-section">
              <h2>Overall</h2>
              <div className="sr-modes">
                {(['both', 'live', 'paper'] as const).map((m) => (
                  <button
                    key={m}
                    className={`sr-mode-pill${aggMode === m ? ' active' : ''}`}
                    onClick={() => setAggMode(m)}
                  >
                    {m === 'both' ? 'Live + Paper' : m === 'live' ? 'Live only' : 'Paper only'}
                  </button>
                ))}
              </div>
              {agg && <MetricTiles m={agg} live={aggMode === 'live'} />}
            </section>

            <section className="sr-section">
              <h2>Equity curve</h2>
              <EquitySpark curve={report.equity_curve} />
            </section>

            <section className="sr-section">
              <h2>By engine</h2>
              <p className="sr-section-hint">
                On-Base · Value · Fair-Fav · Parlay. {corr?.correlation != null
                  ? `On-Base vs Value return correlation ${corr.correlation} over ${corr.n_days} days — low/negative means the engines offset (the portfolio is working).`
                  : 'Engine correlation needs more shared days.'}
              </p>
              <table className="sr-table">
                <thead>
                  <tr>
                    <th>Engine</th><th>Bets</th><th>Strike</th><th>CLV</th><th>ROI</th><th>P&amp;L</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_engine.map((e) => (
                    <MetricsRow key={e.engine} label={e.engine.replace('_', '-')} m={e} />
                  ))}
                </tbody>
              </table>
            </section>

            {sim && (
              <section className="sr-section">
                <h2>Staking comparison</h2>
                <p className="sr-section-hint">{sim.note}</p>
                <table className="sr-table">
                  <thead>
                    <tr><th>Book</th><th>Bets</th><th>Flat ROI</th><th>¼-Kelly ROI</th><th>Dutch ROI</th></tr>
                  </thead>
                  <tbody>
                    {simRow('On-Base', sim.on_base)}
                    {simRow('Value', sim.value)}
                    {simRow('All', sim.all)}
                  </tbody>
                </table>
              </section>
            )}

            <section className="sr-section">
              <h2>Where to focus</h2>
              <p className="sr-section-hint">{report.focus.rule}</p>
              <div className="sr-tiles" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="sr-card">
                  <strong>Do more of</strong>
                  {report.focus.focus.length === 0 ? (
                    <p className="sr-section-hint">No cell has enough sample with positive CLV yet.</p>
                  ) : report.focus.focus.map((c, i) => (
                    <div key={i} className="sr-equity-legend">
                      {c.engine.replace('_', '-')} · {c.day}: <span className="sr-pos">{fmtSignedPct(c.clv)} CLV</span> (n {c.n})
                    </div>
                  ))}
                </div>
                <div className="sr-card">
                  <strong>Review / cut</strong>
                  {report.focus.review.length === 0 ? (
                    <p className="sr-section-hint">Nothing with a reliable negative-CLV read.</p>
                  ) : report.focus.review.map((c, i) => (
                    <div key={i} className="sr-equity-legend">
                      {c.engine.replace('_', '-')} · {c.day}: <span className="sr-neg">{fmtSignedPct(c.clv)} CLV</span> (n {c.n})
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <HonestyNote />
          </>
        )
      )}
    </div>
  );
};

export default StrategyResults;
